import {
  Zap,
  LogOut,
  ChevronDown,
  Settings,
  Bell,
  Users,
  FileText,
  Shield,
  Database,
  FlaskConical,
  KeyRound,
  MessageSquare,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/useAuth";
import { useRole } from "@/hooks/useRole";
import { useNavigate } from "react-router-dom";

interface HeaderProps {
  onShowACHHistory: () => void;
  onSignOut: () => void;
}

function getInitials(displayName?: string | null): string {
  if (!displayName) return "U";
  const words = displayName.trim().split(/\s+/);
  if (words.length === 1) return words[0][0].toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

export function Header({ onShowACHHistory, onSignOut }: HeaderProps) {
  const { profile } = useAuth();
  const { isPAR } = useRole();
  const navigate = useNavigate();
  const initials = getInitials(profile?.display_name);

  return (
    <header className="border-b border-white/[0.06] bg-black/40 backdrop-blur-xl sticky top-0 z-50">
      <div className="w-full px-6 py-2.5">
        <div className="flex items-center justify-between">
          {/* Logo / wordmark */}
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center">
              <Zap className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-base font-bold font-display bg-gradient-to-r from-indigo-400 to-cyan-400 bg-clip-text text-transparent">
              DiscoRun
            </span>
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 gap-1.5 px-2 text-xs">
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500/20 to-cyan-500/20 border border-white/10 flex items-center justify-center text-xs font-medium text-white/80">
                    {initials}
                  </div>
                  <span className="max-w-20 truncate text-white/60">
                    {profile?.display_name || "User"}
                  </span>
                  <ChevronDown className="w-3 h-3 opacity-40" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-52 bg-[hsl(224_40%_7%)] border-white/[0.08]"
              >
                {!isPAR && (
                  <>
                    <DropdownMenuItem onClick={() => navigate("/settings")}>
                      <Settings className="w-4 h-4 mr-2" />
                      Display Settings
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/notifications")}>
                      <Bell className="w-4 h-4 mr-2" />
                      Notifications
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/users")}>
                      <Users className="w-4 h-4 mr-2" />
                      User Management
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={onShowACHHistory}>
                      <FileText className="w-4 h-4 mr-2" />
                      ACH File History
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/backtest")}>
                      <FlaskConical className="w-4 h-4 mr-2" />
                      Backtest Analysis
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/negotiations")}>
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Vendor Negotiations
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/admin")}>
                      <Shield className="w-4 h-4 mr-2" />
                      Admin Panel
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuItem onClick={() => navigate("/par-systems")}>
                  <Database className="w-4 h-4 mr-2" />
                  PAR Systems
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/account-settings")}>
                  <KeyRound className="w-4 h-4 mr-2" />
                  Account Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onSignOut}>
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
}
